import { useEffect, useSyncExternalStore } from 'react';
import { watchPresence, type PresenceUpdate } from '@/lib/realtime';

/**
 * Live, in-memory state from the socket: who is typing where, and who is
 * online. Nothing here is persisted; it fills in as signals arrive.
 */

const TYPING_SHOWN_MS = 6000;

type Listener = () => void;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((l) => l());
const subscribe = (l: Listener) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

// conversationId -> who is typing and until when
let typing = new Map<string, { userId: string; until: number }>();
let presence = new Map<string, PresenceUpdate>();

export function noteTyping(conversationId: string, userId: string): void {
  typing = new Map(typing).set(conversationId, { userId, until: Date.now() + TYPING_SHOWN_MS });
  notify();
  // Clear it when it runs out, unless refreshed meanwhile.
  window.setTimeout(() => {
    const current = typing.get(conversationId);
    if (current && current.until <= Date.now()) {
      typing = new Map(typing);
      typing.delete(conversationId);
      notify();
    }
  }, TYPING_SHOWN_MS + 50);
}

/** A message arrived from them: they've stopped typing. */
export function clearTyping(conversationId: string): void {
  if (!typing.has(conversationId)) return;
  typing = new Map(typing);
  typing.delete(conversationId);
  notify();
}

export function notePresence(updates: PresenceUpdate[]): void {
  if (updates.length === 0) return;
  presence = new Map(presence);
  for (const u of updates) presence.set(u.userId, u);
  notify();
}

/** Who is typing in this thread right now (their user id), if anyone. */
export function useTyping(conversationId: string | null | undefined): string | null {
  return useSyncExternalStore(
    subscribe,
    () => (conversationId ? (typing.get(conversationId)?.userId ?? null) : null),
    () => null
  );
}

export function usePresence(userId: string | null | undefined): PresenceUpdate | undefined {
  return useSyncExternalStore(
    subscribe,
    () => (userId ? presence.get(userId) : undefined),
    () => undefined
  );
}

/** Follow these people's presence while mounted (the inbox's one-to-one contacts). */
export function usePresenceWatch(userIds: string[]): void {
  const key = [...new Set(userIds)].sort().join(',');
  useEffect(() => {
    const ids = key ? key.split(',') : [];
    let cancelled = false;
    void watchPresence(ids).then((states) => {
      if (!cancelled) notePresence(states);
    });
    return () => {
      cancelled = true;
    };
  }, [key]);
}

/** "Online", "Active 5 min ago", "Active yesterday" — or null if unknown. */
export function presenceLabel(p: PresenceUpdate | undefined): string | null {
  if (!p) return null;
  if (p.online) return 'Online';
  if (!p.lastSeenAt) return null;
  const minutes = Math.round((Date.now() - new Date(p.lastSeenAt).getTime()) / 60_000);
  if (minutes < 1) return 'Active just now';
  if (minutes < 60) return `Active ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Active ${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'Active yesterday';
  if (days < 7) return `Active ${days} days ago`;
  return null;
}

// ── Browser alerts (this browser only) ─────────────────────────────────────

export interface AlertPrefs {
  desktop: boolean;
  sound: boolean;
}

const ALERTS_KEY = 'wl.chat.alerts';

export function loadAlertPrefs(): AlertPrefs {
  try {
    const raw = localStorage.getItem(ALERTS_KEY);
    if (raw) return { desktop: false, sound: false, ...JSON.parse(raw) };
  } catch {
    // storage unavailable
  }
  return { desktop: false, sound: false };
}

export function saveAlertPrefs(prefs: AlertPrefs): void {
  try {
    localStorage.setItem(ALERTS_KEY, JSON.stringify(prefs));
  } catch {
    // storage unavailable
  }
}

/** A short two-note chime, made with Web Audio (no sound file to load). */
export function playChime(): void {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.15, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.3);
    });
    window.setTimeout(() => void ctx.close(), 800);
  } catch {
    // audio blocked until the user interacts with the page
  }
}
